import Vue from 'vue'
import { ActionContext, ActionTree } from 'vuex'

import ApiRequest from '@/core/ApiRequest'
import AccessModel from '@/core/models/AccessModel'
import ResponseModel from '@/core/models/ResponseModel'
import RoomModel from '@/core/models/Room'
import UserModel from '@/core/models/User'
import { MenuGroup, MenuItem } from '@/core/models/Menu'
import Event from '@/core/models/Event'
import Call from '@/core/models/Call'
import RootState from '@/store/root/state'
import * as mutations from '@/store/root/mutations'


export const FETCH_ROOMS_LIST: string = 'fetchRoomsList'
export const ADD_EVENT_ITEM: string = 'addEventItem'

export const PASSWORD_CHANGE_REQUEST: string = 'passwordChangeRequest'
export const PASSWORD_SET_FOR_REQUEST: string = 'passwordSetForRequest'

export const FETCH_USERS_LIST: string = 'fetchUsersList'
export const FETCH_MENU_LIST: string = 'fetchMenuList'
export const ADD_USER_ITEM: string = 'addUserItem'
export const ADD_MENU_ITEM: string = 'addMenuItem'
export const SWAP_MENU_ITEMS: string = 'swapMenuItems'
export const UPDATE_USER_ITEM: string = 'updateUserItem'
export const UPDATE_MENU_ITEM: string = 'updateMenuItem'
export const UNLOCK_USER: string = 'unlockUser'
export const LOCK_USER: string = 'lockUser'
export const LOG_IN_USER: string = 'logInUser'

export const FETCH_HOLIDAY: string = 'fetchHoliday'
export const FETCH_HOLIDAYS: string = 'fetchHolidays'
export const ADD_HOLIDAY: string = 'addHoliday'
export const DELETE_HOLIDAY: string = 'deleteHoliday'
export const DELETE_MENU_ITEM: string = 'deleteMenuItem'

export const FETCH_CLIENT: string = 'fetchClient'

export const ADD_CALL_ITEM: string = 'addCallItem'

/**
 * Fetch rooms list.
 *
 * @param store   Root Vuex store.
 *
 * @returns       Resolved promise.
 */
export function fetchRoomsList (
  store: ActionContext<RootState, any>,
) {
  return store.getters.getApiRequest.send(
    'GET',
    'rooms',
  ).then((response: ResponseModel) => {
    store.state.roomsList = response.data
  })
}

/**
 * Fetch users list.
 *
 * @param store   Root Vuex store.
 *
 * @returns       Resolved promise.
 */
export function fetchUsersList (
  store: ActionContext<RootState, any>,
) {
  return store.getters.getApiRequest.send(
    'GET',
    'users',
  ).then((response: ResponseModel) => {
    store.state.usersList = response.data // todo: destruct data and create objects
  })
}

/**
 * Fetch menu list.
 *
 * @param store   Root Vuex store.
 *
 * @returns       Resolved promise.
 */
export function fetchMenuList (
  store: ActionContext<RootState, any>,
) {
  return store.getters.getApiRequest.send(
    'GET',
    'menu',
  ).then((response: ResponseModel) => {
    if (!response?.data) {
      store.state.menuList = []
      return
    }

    const groups = response.data
    for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
      const group = groups[groupIndex]
      if (group.items && group.items.length) {
        for (let itemIndex = 0; itemIndex < group.items.length; itemIndex++) {
          const item = group.items[itemIndex] as any
          groups[groupIndex].items[itemIndex] = {
            id: item.id,
            title: item.title,
            extraPrice: item.extra_price,
            groupId: item.group_id,
          }
        }
      }
    }
    store.state.menuList = groups
  })
}

/**
 * Add event item.
 *
 * @param store   Root Vuex store.
 * @param payload   Payload with event to be added.
 *
 * @returns       Resolved promise.
 */
export function addEventItem (
  store: ActionContext<RootState, any>,
  payload: { event: Event, forced: boolean | null }
): Promise<ResponseModel> {
  return store.getters.getApiRequest.send(
    'POST',
    'events',
    JSON.stringify({ event: payload.event, forced: payload.forced }),
  )
}

/**
 * Add call item.
 *
 * @param store   Root Vuex store.
 * @param payload   Payload with call to be added.
 *
 * @returns       Resolved promise.
 */
export function addCallItem (
  store: ActionContext<RootState, any>,
  payload: { call: Call, forced: boolean | null }
): Promise<ResponseModel> {
  return store.getters.getApiRequest.send(
    'POST',
    'calls',
    JSON.stringify({ event: payload.call, forced: payload.forced }),
  )
}

/**
 * Add user item.
 *
 * @param store   Root Vuex store.
 * @param user    User to be added.
 *
 * @returns       Resolved promise.
 */
export function addUserItem (
  store: ActionContext<RootState, any>,
  user: UserModel,
) {
  return store.getters.getApiRequest.send(
    'POST',
    'users',
    JSON.stringify({ user }),
  )
}

/**
 * Add menu item.
 *
 * @param store   Root Vuex store.
 * @param item    Menu item to be added.
 *
 * @returns       Resolved promise.
 */
export function addMenuItem (
  store: ActionContext<RootState, any>,
  item: MenuItem,
) {
  return store.getters.getApiRequest.send(
    'POST',
    'menu/' + item.groupId + '/items',
    JSON.stringify({
      id: item.id,
      title: item.title,
      extra_price: +item.extraPrice,
      group_id: item.groupId,
    }),
  )
}

/**
 * Swap menu items.
 *
 * @param store   Root Vuex store.
 * @param payload    Menu items to be swapped.
 *
 * @returns       Resolved promise.
 */
export function swapMenuItems (
  store: ActionContext<RootState, any>,
  payload: {item1: MenuItem, item2: MenuItem},
) {
  const groupIndex = store.state.menuList.findIndex((group) => (group.id === payload.item1.groupId))

  return store.getters.getApiRequest.send(
    'POST',
    'menu/' + payload.item1.groupId + '/swap',
    JSON.stringify({ id1: payload.item1.id, id2: payload.item2.id }),
  ).then((response: ResponseModel) => {
    if (response && response.data && response.data.item1 && response.data.item2) {
      // todo: move to mutations
      const id1Index = store.state.menuList[groupIndex].items.findIndex((item) => (item.id === payload.item1.id))
      const id2Index = store.state.menuList[groupIndex].items.findIndex((item) => (item.id === payload.item2.id))

      Vue.set(store.state.menuList[groupIndex].items, id1Index, {
        ...response.data.item2,
        extraPrice: response.data.item2.extra_price,
        groupId: response.data.item2.group_id,
      })
      Vue.set(store.state.menuList[groupIndex].items, id2Index, {
        ...response.data.item1,
        extraPrice: response.data.item1.extra_price,
        groupId: response.data.item2.group_id,
      })
    }
  })
}

/**
 * Update user item.
 *
 * @param store   Root Vuex store.
 * @param user    User to be updated.
 *
 * @returns       Resolved promise.
 */
export function updateUserItem (
  store: ActionContext<RootState, any>,
  user: UserModel,
) {
  return store.getters.getApiRequest.send(
    'POST',
    'users/' + user.id,
    JSON.stringify({ user }),
  )
}

/**
 * Update menu item.
 *
 * @param store   Root Vuex store.
 * @param item    Menu item to be updated.
 *
 * @returns       Resolved promise.
 */
export function updateMenuItem (
  store: ActionContext<RootState, any>,
  item: MenuItem,
) {
  return store.getters.getApiRequest.send(
    'POST',
    'menu/' + item.groupId + '/items/' + item.id,
    JSON.stringify({
      id: item.id,
      title: item.title,
      extra_price: +item.extraPrice,
      group_id: item.groupId,
    }),
  ).then((response: ResponseModel) => {
    if (response && response.data && response.data.item) {
      // todo: move to mutations
      const item = response.data.item
      const groupIndex = store.state.menuList.findIndex((group) => (group.id === item.group_id))
      const itemIndex = store.state.menuList[groupIndex].items.findIndex((i) => (i.id === item.id))
      console.log('before=', JSON.stringify(store.state.menuList[groupIndex].items[itemIndex]))
      console.log('item=', JSON.stringify(item))
      Vue.set(store.state.menuList[groupIndex].items, itemIndex, {
        ...item,
        extraPrice: item.extra_price,
        groupId: item.group_id,
      })
      console.log('after=', JSON.stringify(store.state.menuList[groupIndex].items[itemIndex]))
    }
  })
}

/**
 * Create password change request.
 *
 * @param store   Root Vuex store.
 * @param login   Login of user.
 *
 * @returns       Resolved promise.
 */
export function passwordChangeRequest (
  store: ActionContext<RootState, any>,
  login: string,
) {
  return store.getters.getApiRequest.send(
    'POST',
    'users/password-change-requests',
    JSON.stringify({ login }),
    false,
    false
  )
}

/**
 * Set password by request.
 *
 * @param store     Root Vuex store.
 * @param payload   Token and new password.
 *
 * @returns       Resolved promise.
 */
export function passwordSetForRequest (
  store: ActionContext<RootState, any>,
  payload: { token: string, password: string },
) {
  const { token, password } = payload
  return store.getters.getApiRequest.send(
    'POST',
    'users/password-change-requests/' + token,
    JSON.stringify({ password }),
    false,
    false
  )
}

/**
 * Lock user.
 *
 * @param store   Root Vuex store.
 * @param user    User to be updated.
 *
 * @returns       Resolved promise.
 */
export function lockUser (
  store: ActionContext<RootState, any>,
  user: UserModel,
) {
  return store.getters.getApiRequest.send(
    'POST',
    'users/' + user.id + '/status',
    JSON.stringify({ status: 'inactive' }),
  )
}

/**
 * Unlock user.
 *
 * @param store   Root Vuex store.
 * @param user    User to be updated.
 *
 * @returns       Resolved promise.
 */
export function unlockUser (
  store: ActionContext<RootState, any>,
  user: UserModel,
) {
  return store.getters.getApiRequest.send(
    'POST',
    'users/' + user.id + '/status',
    JSON.stringify({ status: 'active' }),
  )
}

/**
 * Log in user.
 *
 * @param store     Root Vuex store.
 * @param payload   User`s login and password.
 *
 * @returns         Resolved promise.
 */
export function logInUser (
  store: ActionContext<RootState, any>,
  payload: { login: string, password: string },
) {
  const { login, password } = payload
  return store.getters.getApiRequest.send(
    'POST',
    'auth',
    JSON.stringify({ login, password }),
    true
  ).then((response: ResponseModel) => {
    const { access_token, refresh_token } = response.data
    const accessModel = new AccessModel(access_token, refresh_token)
    store.commit(mutations.SET_ACCESS_MODEL, accessModel)

    return store.getters.getApiRequest.send(
      'GET',
      'auth',
    )
  }).then((response: ResponseModel) => {
    const user = response.data
    const accessModel = store.getters.getAccessModel
    accessModel.issuedAt = user.iat
    accessModel.expiredAt = user.exp

    accessModel.user = new UserModel()
    accessModel.user.id = user.id
    accessModel.user.username = user.username
    accessModel.user.fullname = user.fullname
    accessModel.user.email = user.email
    accessModel.user.role = user.role
    accessModel.user.created_at = user.created_at

    store.commit(mutations.SET_ACCESS_MODEL, accessModel)
  })
}

/**
 * Fetch holiday.
 *
 * @param store   Root Vuex store.
 * @param day     Day to fetch.
 *
 * @returns       Resolved promise.
 */
export function fetchHoliday (
  store: ActionContext<RootState, any>,
  day: string,
) {
  return store.getters.getApiRequest.send(
    'GET',
    'calendar/holidays/' + encodeURIComponent(day),
  ).then((response: ResponseModel) => response.data)
    .catch(() => null)
}

/**
 * Fetch holidays.
 *
 * @param store   Root Vuex store.
 * @param payload Day from and day to.
 * @returns       Resolved promise.
 */
export function fetchHolidays (
  store: ActionContext<RootState, any>,
  payload: { from: string, to: string },
) {
  return store.getters.getApiRequest.send(
    'GET',
    'calendar/holidays?from=' + encodeURIComponent(payload.from) + '&to=' + encodeURIComponent(payload.to),
  ).then((response: ResponseModel) => response.data)
    .catch(() => [])
}

/**
 * Add holiday.
 *
 * @param store   Root Vuex store.
 * @param payload Day and text of holiday.
 *
 * @returns       Resolved promise.
 */
export function addHoliday (
  store: ActionContext<RootState, any>,
  payload: { day: string, text: string },
) {
  const { day, text } = payload
  return store.getters.getApiRequest.send(
    'POST',
    'calendar/holidays',
    JSON.stringify({ day, text }),
  )
}

/**
 * Delete holiday.
 *
 * @param store   Root Vuex store.
 * @param day     Day to delete from holiday.
 *
 * @returns       Resolved promise.
 */
export function deleteHoliday (
  store: ActionContext<RootState, any>,
  day: string,
) {
  return store.getters.getApiRequest.send(
    'DELETE',
    'calendar/holidays/' + encodeURIComponent(day),
  )
}

/**
 * Delete menu item.
 *
 * @param store   Root Vuex store.
 * @param item    Item to delete from menu.
 *
 * @returns       Resolved promise.
 */
export function deleteMenuItem (
  store: ActionContext<RootState, any>,
  item: MenuItem,
) {
  return store.getters.getApiRequest.send(
    'DELETE',
    'menu/' + item.groupId + '/items/' + item.id,
  ).then((response: ResponseModel) => {
    if (response && response.data) {
      // todo: move to mutations
      const groupIndex = store.state.menuList.findIndex((group) => (group.id === item.groupId))
      const itemIndex = store.state.menuList[groupIndex].items.findIndex((i) => (i.id === item.id))
      store.state.menuList[groupIndex].items.splice(itemIndex, 1)
    }
  })
}


/**
 * Fetch client.
 *
 * @param store   Root Vuex store.
 * @param id      Client ID.
 *
 * @returns       Resolved promise.
 */
export function fetchClient (
  store: ActionContext<RootState, any>,
  id: string,
) {
  return store.getters.getApiRequest.send(
    'GET',
    'clients/' + encodeURIComponent(id),
  ).then((response: ResponseModel) => response.data)
    .catch(() => null)
}


export default {
  addEventItem,
  addUserItem,
  addMenuItem,
  swapMenuItems,
  fetchRoomsList,
  fetchUsersList,
  fetchMenuList,
  logInUser,
  updateUserItem,
  updateMenuItem,
  passwordChangeRequest,
  passwordSetForRequest,
  unlockUser,
  lockUser,
  fetchHoliday,
  fetchHolidays,
  addHoliday,
  deleteHoliday,
  deleteMenuItem,
  fetchClient,
  addCallItem,
} as ActionTree<RootState, any>
